import React from "react";
import LmsFormGenerator, { BCFormController, IFormGeneratorFieldOptions } from "../ui/LmsFormGenerator";
import LmsTable from "../ui/LmsTable";
import LmsTabPages from "../ui/LmsTabPages";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { LmsEndpoints } from "../../constants/LmsEndpoints";
import { useFetchData, useParseRouteActions, getMaskedString } from "../../../helpers/GlobalHelper";
import { AddInsuranceForm, AddStateTaxForm, EditStateTaxForm, EditInsuranceForm } from "../ui/LmsSharedForms";
import { CommentsTable, BanksTable, StateTaxTable, InsuranceTable, ExistingContactTable } from "../ui/LmsSharedTables";
import { IDefaultPagesProps } from "../../interfaces/Lms";
import { addBankFields } from "./LmsOrganizationsBankAccounts";

const RoutingsTable = ({ suffix = "", orgId }) => {
  const actionsButtons = [
    { text: "Add", path: `/organizations/addrouting?orgId=${orgId}` },
    { text: "Edit", path: "/organizations/editrouting/{id}?orgId=${orgId}" },
    "remove",
    "export",
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    deleteSuffix="bankaccount/bank-routings"
    sortable={["Created On"]}
    onClickPath="/organizations/editrouting"
    columnLabelsDictionary={{paymentType: "Transfer Method", routingCode: "Routing Number"}}
  />;
};

const AccountsTable = ({ suffix = "", orgId }) => {
  const actionsButtons = [
    { text: "Add", path: `/organizations/addaccount?orgId=${orgId}` },
    { text: "Edit", path: "/organizations/editaccount" },
    "remove",
    "export",
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    deleteSuffix="orgs/bank-infos"
    sortable={["Created On"]}
    onClickPath={"/organizations/editaccount"}
    useGlobalDictionary
  />;
};

const LoansTable = ({ suffix = "" }) => {
  return <LmsTable
    actionButtons={["export"]}
    apiSuffix={suffix}
  />;
};

const AccountsTitleCompanyTable = ({ suffix, orgId }) => {
  const actionsButtons = [
    { text: "Add", path: `/organizations/addtitlecompanyaccount?orgId=${orgId}` },
    { text: "Edit", path: `/organizations/edittitlecompanyaccount/{id}?orgId=${orgId}` },
    "remove",
    "export",
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    deleteSuffix="orgs/bank-infos"
    sortable={["Created On"]}
    onClickPath={"/organizations/edittitlecompanyaccount"}
    useGlobalDictionary
  />;
};

export const LicensesTable = ({ suffix = "", orgId = "" }) => {
  const actionsButtons = [
    { text: "Add", path: `/organizations/addlicense/?orgId=${orgId}` },
    { text: "Edit", path: `/organizations/editlicense/:id?orgId=${orgId}` },
    "remove",
    "export",
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    deleteSuffix="orgs/licenses"
    sortable={["Created On"]}
    onClickPath="/organizations/editlicense"
  />;
};

const ContactsVendorTable = ({ suffix = "", orgId }) => {
  const actionsButtons = [
    { text: "Add", path: `/organizations/addexistingcontact?orgId=${orgId}` },
    "remove",
    "export"
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    deleteSuffix="orgs/contact-orgs"
    sortable={["Created On"]}
    onClickPath="/organizations-contacts/edit"
    columnLabelsDictionary={GlobalConstants.ContactsTableColumnLabelsDictionary}
    skipClick
  />;
};

const ContactsBrokerageTable = ({ suffix = "", orgId }) => {
  const actionsButtons = [
    { text: "Add New", action: "add", path: `/organizations-contacts/add?orgId=${orgId}` },
    { text: "Add Existing", action: "add", path: `/organizations/addexistingcontact?orgId=${orgId}` },
    { action: "edit", path: "/organizations-contacts" },
    GlobalConstants.TableDefaultActions.DELETE,
    GlobalConstants.TableDefaultActions.EXPORT,
    GlobalConstants.TableDefaultActions.ACTIVATE,
    GlobalConstants.TableDefaultActions.DEACTIVATE
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    deleteSuffix="orgs/contacts"
    activateSuffix="orgs/activate-contacts"
    deActivateSuffix="orgs/deactivate-contacts"
    onClickPath="/organizations-contacts"
    columnLabelsDictionary={GlobalConstants.ContactsTableColumnLabelsDictionary}
  />;
};

const BorrowersTable = ({ suffix = "" }) => {
  const actionsButtons = ["export"];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    sortable={["Created On"]}
  />;
};

const LoanPropertiesTable = ({ suffix = "", orgId }) => {
  const actionsButtons = [
    { text: "Add", path: `/organizations/addloanproperty?orgId=${orgId}` },
    "export"
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix}
    sortable={["Created On"]}
    skipClick={true}
  />;
};

const addAccountFormFields = (myForm, data = null as any, isAdd = false) => {
  myForm.addCenteredGroup([myForm.getDropdownField("Organization", "/orgs/list-borrower-orgs", { name: "organizationId", required: true, dropdownSkipCache: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Special Purpose Entity", LmsEndpoints.Orgs.GET.LIST_SPES, {
    name: "specialPurposeEntityId",
    dependencyField: "organizationId"
  })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Bank", "orgs/list-bank-orgs", { name: "bankOrganizationId", required: true, disabled: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Bank Account Number", { placeHolder: getMaskedString(data?.bankAccountNumber), value: "", required: isAdd })]);
  myForm.addCenteredGroup([myForm.getInputField("Street On Bank Account", { name: "addressOnBankAccount" })]);
  myForm.addCenteredGroup([myForm.getInputField("Name On Bank Account")]);
  myForm.addCenteredGroup([myForm.getInputField("City On Bank Account")]);
  myForm.addCenteredGroup([myForm.getInputField("State On Bank Account")]);
  myForm.addCenteredGroup([myForm.getInputField("Zip On Bank Account")]);
  myForm.addCenteredGroup([myForm.getDropdownField("Bank Routing", "bankaccount/list-bank-routing",{ name: "bankRoutingId", dependencyField: "bankOrganizationId" })]);
  myForm.addCenteredGroup([myForm.getInputField("Reference Detail")]);
  myForm.addCenteredGroup([
    myForm.getCheckboxField("Is Default", { width: 3 }),
    myForm.getCheckboxField("Is Active", isAdd ? { value: true, width: 3 } : { width: 3 }),
  ], { blankWidth: 5 });

  return myForm;
};

const AddAccountForm = ({ orgId }) => {
  const myForm = addAccountFormFields(new BCFormController(orgId ? { bankOrganizationId: orgId } : {}, { submitSuffix: LmsEndpoints.Orgs.POST.BANK_INFO }), null, true);

  return <LmsFormGenerator myForm={myForm} actions={["save-new", "save-close", "close"]} />;
};

const EditAccountForm = ({ id }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`orgs/bank-info/${id}`);
  const myForm = new BCFormController(data, { submitSuffix: LmsEndpoints.Orgs.PUT.BANK_INFO, clearSuffixesOnSubmit: [`orgs/bank-info/${id}`] });
  return <LmsFormGenerator myForm={addAccountFormFields(myForm, data)} isLoading={isLoading} error={error} actions={["save-new", "save-close", "close"]} />;
};

const addTitleCompanyAccountFormFields = (myForm: BCFormController, data = null as any, isAdd = false) => {
  myForm.addCenteredGroup([myForm.getDropdownField("Organization", LmsEndpoints.Orgs.GET.LIST_ORGS, { name: "organizationId", required: true, disabled: true, dropdownSkipCache: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Special Purpose Entity", LmsEndpoints.Orgs.GET.LIST_SPES, { name: "specialPurposeEntityId", dependencyField: "organizationId" })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Bank", "orgs/list-bank-orgs", { name: "bankOrganizationId", required: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Bank Account Number", { placeHolder: getMaskedString(data?.bankAccountNumber), value: "", required: isAdd })]);
  myForm.addCenteredGroup([myForm.getInputField("Street On Bank Account", { name: "addressOnBankAccount" })]);
  myForm.addCenteredGroup([myForm.getInputField("Name On Bank Account")]);
  myForm.addCenteredGroup([myForm.getInputField("City On Bank Account")]);
  myForm.addCenteredGroup([myForm.getInputField("State On Bank Account")]);
  myForm.addCenteredGroup([myForm.getInputField("Zip On Bank Account")]);
  myForm.addCenteredGroup([myForm.getDropdownField("Bank Routing", myForm.values.organizationId ? `bankaccount/list-bank-routing/{id}?org=${myForm.values.organizationId}` : "", {
    name: "bankRoutingId",
    dependencyField: "bankOrganizationId",
  })]);
  myForm.addCenteredGroup([myForm.getInputField("Reference Detail")]);
  myForm.addCenteredGroup([
    myForm.getCheckboxField("Is Default", { width: 3 }),
    myForm.getCheckboxField("Is Active", isAdd ? { value: true, width: 3 } : { width: 3 }),
  ], { blankWidth: 5 });

  return myForm;
};

const AddTitleCompanyAccountForm = ({ orgId }) => {
  const myForm = addTitleCompanyAccountFormFields(new BCFormController(orgId ? { organizationId: orgId } : {}, { submitSuffix: LmsEndpoints.Orgs.POST.BANK_INFO }), null, true);

  return <LmsFormGenerator myForm={myForm} actions={["save-new", "save-close", "close"]} />;
};

const EditTitleCompanyAccountForm = ({ id }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`orgs/bank-info/${id}`);
  const myForm = new BCFormController(data, {
    submitSuffix: LmsEndpoints.Orgs.PUT.BANK_INFO,
    clearSuffixesOnSubmit: [`orgs/bank-info/${id}`],
    deleteSuffix: "orgs/bank-infos"
  });

  return <LmsFormGenerator myForm={addTitleCompanyAccountFormFields(myForm, data, false)} isLoading={isLoading} error={error} />;
};

export const addRoutingFormFields = (myForm) => {
  myForm.addCenteredGroup([myForm.getDropdownField("Bank", "orgs/list-bank-orgs", { name: "bankOrganizationId", disabled: true, required: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Transfer Method", LmsEndpoints.Orgs.GET.TRANSFER_METHOD, { name: "paymentMethodId", required: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Routing Number", { fieldType: "number", name: "number", required: true, validations: ["routingNumber"] })]);
  return myForm;
};

const AddRoutingForm = ({orgId}) => {
  const myForm = addRoutingFormFields(new BCFormController(orgId ? { bankOrganizationId: orgId } : {}, { submitSuffix: "bankaccount/create-bank-routing" }));
  return <LmsFormGenerator myForm={myForm} />;
};

const EditRoutingForm = ({ id }) => {
  const [{ data, isLoading, error }] = useFetchData(`bankaccount/routings/${id}`);
  const myForm = addRoutingFormFields(new BCFormController(data, {
    submitSuffix: "bankaccount/update-bank-routing",
    deleteSuffix: "bankaccount/bank-routings",
    clearSuffixesOnSubmit: [`bankaccount/routings/${id}`]
  }));

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} />;
};

const addLicenceFields = (myForm) => {
  myForm.addCenteredGroup([myForm.getDropdownField("Organization", "orgs/list-orgs", { name: "organizationId", disabled: true, dropdownSkipCache: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("State", "orgs/list-states", { name: "stateId" })]);
  myForm.addCenteredGroup([myForm.getInputField("License No", { required: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Description", { field: "textarea" })]);
  return myForm;
};

const AddLicenseForm = ({ orgId }) => {
  const myForm = addLicenceFields(new BCFormController(orgId ? { organizationId: orgId } : {}, {
    submitSuffix: "orgs/license",
    saveAndContinueSuffix: "organizations/editlicense"
  }));

  return <LmsFormGenerator myForm={myForm} />;
};

const EditLicenseForm = ({ id }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`orgs/license/${id}`);
  const myForm = addLicenceFields(new BCFormController(data, {
    submitSuffix: "orgs/update-license",
    deleteSuffix: "orgs/licenses",
    clearSuffixesOnSubmit: [`orgs/license/${id}`]
  }));
  const formActions = ["save", "save-close", "delete", "close"];

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={formActions} />;
};

const AddLoanProperty = ({ orgId }) => {
  const [{ fetchData }] = useFetchData();
  const handleOnFormChange = async (options, lastUpdated) => {
    if (lastUpdated === "loanId" && options?.inputs?.loanId) {
      const loanId = options?.inputs?.loanId;
      const loanDetails = await fetchData(`/loans/${loanId}/details`);
      if (loanDetails?.data?.borrowerOrganizationId) {
        options?.setInputs({ ...options?.inputs, borrowerOrganizationId: loanDetails?.data?.borrowerOrganizationId });
      }
    }
    if (lastUpdated === "borrowerOrganizationId" && options?.inputs?.borrowerOrganizationId) {
      options?.setInputs({ ...options?.inputs, loanId: undefined });
    }
  };

  const myForm = new BCFormController({ orgId }, { submitSuffix: "loanproperty/link-bankinfo" });
  myForm.addCenteredGroup([myForm.getDropdownField("Borrower", "orgs/list-borrower-orgs", { name: "borrowerOrganizationId", required: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Special Purpose Entity", "orgs/list-special-purpose-entities", { dependencyField: "borrowerOrganizationId" })]);
  myForm.addCenteredGroup([
    myForm.getDropdownField("Loan", `loans/list-borrower-loans?orgId=${orgId}`, {
      required: true,
      optionalDropdownQueries: [
        { query: "borrowerOrganizationId", fieldName: "borrowerOrganizationId" },
        { query: "specialPurposeEntityId", fieldName: "specialPurposeEntityId" },
      ],
    }),
  ]);

  myForm.addCenteredGroup([myForm.getDropdownField("Loan Property", "loans/list-properties-without-titlecompany", { dependencyField: "loanId", required: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Bank", orgId ? `metadata/list-bank-info/${orgId}`: "", { required: true, name: "titleCompanyBankInformationId" })]);

  return <LmsFormGenerator myForm={myForm} actions={["save-close", "close"]} onFormChanged={handleOnFormChange} />;
};

const ViewBankForm = ({ id }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`orgs/bank-info/${id}`);
  const myForm = addBankFields(new BCFormController(data, { initIsDisabled: true }));

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={["close"]} />;
};

export const addOrganizationFormFields = (myForm, orgInfoData = null as any, isAdd = false, isTypeDisabled = false) => {
  const fileFieldOptions = {
    name: "assetFile",
    documentFileName: "documentFileName",
    documentDownloadSuffix: "orgs/download-doc/{id}",
  } as IFormGeneratorFieldOptions;

  myForm.addGroupFields([
    myForm.getDropdownField("Organization Type", "metadata/list-org-types", { required: true, name: "organizationTypeId", disabled: isTypeDisabled }),
    myForm.getInputField("Organization Name", { name: "name", required: true }),
    myForm.getInputField("Internal Abbreviation", { name: "abbreviation", validations: ["abbreviation"] }),
    myForm.getDropdownField("Type", "metadata/list-entity-sub-types", { required: true, name: "entitySubTypeId" }),
    myForm.getCheckboxField("Is Active", isAdd ? { value: true } : {})
  ]);
  myForm.addGroupFields([
    myForm.getInputField("Current Default", { disabled: true }),
    myForm.getCheckboxField("Default Broker"),
    myForm.getBlankField(),
    myForm.getBlankField(),
    myForm.getBlankField()
  ], { isHidden: { organizationTypeId: GlobalConstants.OrganizationTypes.Brokerage }, isHiddenOperation: "notequal" });
  myForm.addGroupFields([
    myForm.getDisplayField("Current Default", { value: orgInfoData?.currentDeafult || "" }),
    myForm.getFileField("Document", fileFieldOptions),
    myForm.getCheckboxField("Default Bank"),
    myForm.getCheckboxField("Assignment Not Required"),
    myForm.getBlankField()
  ], { isHidden: { organizationTypeId: GlobalConstants.OrganizationTypes.Bank }, isHiddenOperation: "notequal" });

  myForm.addAddressSectionToForm(false, { isHidden: { organizationTypeId: GlobalConstants.OrganizationTypes.Bank } });

  myForm.addGroupFields([
    myForm.getInputField("Website", { validations: ["website"] }),
    myForm.getInputField(
      "EIN/TIN",
      {
        name: "employerIdentificationNumber",
        isHidden: { organizationTypeId: GlobalConstants.OrganizationTypes.Bank },
        validations: ["EIN/TIN"]
      }
    ),
    myForm.getInputField("Business Phone", { validations: ["phone"], fieldType: "phone" }),
    myForm.getInputField("Email", { validations: ["email"] }),
    myForm.getInputField("Description"),
    myForm.getBlankField({ isHidden: { organizationTypeId: GlobalConstants.OrganizationTypes.Bank }, isHiddenOperation: "notequal" }),
  ]);
  if (myForm?.metaData?.hasContactFirstLastName) {
    myForm.addGroupFields([
      myForm.getInputField("Contact First Name", { required: true }),
      myForm.getInputField("Contact Last Name", { required: true }),
      myForm.getBlankField(),
      myForm.getBlankField(),
      myForm.getBlankField(),
    ]);
  }
  myForm.addGroupFields([
    myForm.getFileField("Document", fileFieldOptions),
    myForm.getInputField("Quick Books File Name"),
    myForm.getInputField("Sub Domain"),
    myForm.getCheckboxField("Assignment Not Required", isAdd ? { value: true } : {}),
    myForm.getCheckboxField("Draw Approval Exception Applicable"),
  ], { isHidden: { organizationTypeId: GlobalConstants.OrganizationTypes["Loan Company"] }, isHiddenOperation: "notequal" });
  if (isAdd) {
    myForm.addGroupFields([
      myForm.getInputField("Quick Books File Name"),
      myForm.getInputField("Net Suite Subsidiary"),
      myForm.getFileField("Document", fileFieldOptions),
      myForm.getCheckboxField("Assignment Not Required"),
      myForm.getBlankField()
    ], { isHidden: { organizationTypeId: GlobalConstants.OrganizationTypes.Fund }, isHiddenOperation: "notequal" });
  }
  else {
    myForm.addGroupFields([
      myForm.getInputField("Quick Books File Name"),
      myForm.getInputField("Net Suite Subsidiary"),
      myForm.getFileField("Document", fileFieldOptions),
      myForm.getDropdownField("Fund Signor", orgInfoData ? `orgs/list-fund-signor-contacts/${orgInfoData.id}` : ""),
      myForm.getCheckboxField("Assignment Not Required"),
    ], { isHidden: { organizationTypeId: GlobalConstants.OrganizationTypes.Fund }, isHiddenOperation: "notequal" });
  }
  myForm.addGroupFields([
    myForm.getNumberField("Lead Time In Days", { required: true }),
    myForm.getBlankField(),
    myForm.getBlankField(),
    myForm.getBlankField(),
    myForm.getBlankField(),
  ], { isHidden: { organizationTypeId: GlobalConstants.OrganizationTypes.Inspector }, isHiddenOperation: "notequal" });
  return myForm as BCFormController;
};

const AddOrganization = ({ clearSuffixes }) => {
  const submitSuffix = LmsEndpoints.Orgs.POST.ORG;
  const deleteSuffix = LmsEndpoints.Orgs.DELETE.ORGS;
  const myForm = addOrganizationFormFields(new BCFormController({}, {
    submitSuffix,
    payloadDataType: "FormData",
    deleteSuffix,
    clearSuffixesOnSubmit: clearSuffixes,
    excludeFromSubmit: ["document"]
  }), null, true);

  return <LmsFormGenerator myForm={myForm} />;
};

const EditOrganization = ({ ...props }) => {
  const { orgInfoData, clearSuffixes, isLoading, error } = props;
  const submitSuffix = LmsEndpoints.Orgs.PUT.ORG;
  const deleteSuffix = LmsEndpoints.Orgs.DELETE.ORGS;
  const { Brokerage, Bank } = GlobalConstants.OrganizationTypes;
  const hasDefaultBroker = orgInfoData?.organizationTypeId === Brokerage || orgInfoData?.organizationTypeId === Bank;
  const [{ data: currentDefaultData }] = useFetchData(hasDefaultBroker ? "orgs/default-broker" : "");
  const currentDefault = currentDefaultData && currentDefaultData?.defaultBroker ? { currentDefault: currentDefaultData?.defaultBroker } : {};
  const myForm = addOrganizationFormFields(new BCFormController(currentDefaultData ? { ...currentDefault, ...orgInfoData } : orgInfoData, {
    submitSuffix,
    deleteSuffix,
    payloadDataType: "FormData",
    clearSuffixesOnSubmit: clearSuffixes,
    excludeFromSubmit: ["currentDefault"]
  }), orgInfoData);

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={["save", "save-close", "edit", "delete", "close"]} />;
};

const addCommentFields = (myForm) => {
  myForm.addCenteredGroup([myForm.getDropdownField("Organization", "orgs/list-orgs", { required: true, disabled: true, dropdownSkipCache: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Category", "orgs/list-comment-categories", { name: "organizationCommentCategoryId", required: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Subject", { required: true })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Comments", { required: true,  name: "description" })]);
  return myForm;
};

const AddCommentToOrganizationForm = ({ orgId }) => {
  const myForm = addCommentFields(new BCFormController({ organizationId: orgId }, { submitSuffix: "orgs/comment" }));

  return <LmsFormGenerator myForm={myForm} actions={["save-new", "save-close", "close"]}/>;
};

const ViewCommentForm = ({ id }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`orgs/comment/${id}`);
  const myForm = addCommentFields(new BCFormController(data, { initIsDisabled: true }));

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={["close"]} />;
};

const LmsOrganizationsTable = ({ suffix }) => {
  const actionButtons = [
    "add",
    "edit",
    "remove",
    "export",
    { text: "Copy Borrower", action: "copy", path: "organizations-borrowers/copy" }
  ];

  return <LmsTable
    actionButtons={actionButtons as any}
    apiSuffix={suffix}
    deleteSuffix={LmsEndpoints.Orgs.DELETE.ORGS}
    sortable={["Created On"]}
    columnLabelsDictionary={{
      name: "Organization Name",
      entitySubType: "Type",
      abbreviation: "Internal Abbreviation",
      employerIdentificationNumber: "EIN/TIN",
    }}
  />;
};

const LmsOrganizationsEdit = ({ orgId, mainTableSuffix }) => {
  const getRecordSuffix = `orgs/info/${orgId}`;
  const commentsSuffix = `orgs/comments/${orgId}`;
  const clearSuffixes = [mainTableSuffix, getRecordSuffix, "orgs/list-orgs"];
  let tabPages: Record<string, any> | undefined;
  const skipTableTypes = [
    GlobalConstants.OrganizationTypes.Escrow,
    GlobalConstants.OrganizationTypes.Appraiser,
    GlobalConstants.OrganizationTypes.Insurance,
    GlobalConstants.OrganizationTypes.Other,
    GlobalConstants.OrganizationTypes["Subordinate Lender"],
  ];

  const [{ data: orgInfoData, error, isLoading }] = useFetchData(orgId ? `orgs/info/${orgId}` : "");
  const organizationTypeId = orgInfoData?.organizationTypeId;

  if (organizationTypeId === GlobalConstants.OrganizationTypes["Title Company"]) {
    tabPages = {
      Accounts: <AccountsTitleCompanyTable suffix={`orgs/accounts/${orgId}`} orgId={orgId}/>,
      "Loan Properties": <LoanPropertiesTable suffix={`loanproperty/loan-property-bank-info/${orgId}`} orgId={orgId} />,
      Loans: <LoansTable suffix={`loandetails/list/${orgId}`} />
    };
  }

  if (organizationTypeId === GlobalConstants.OrganizationTypes.Bank) {
    tabPages = {
      Routings: <RoutingsTable suffix={`orgs/org-bank-routings/${orgId}`} orgId={orgId} />,
      Accounts: <AccountsTable suffix={`orgs/accounts/${orgId}`} orgId={orgId} />,
      Loans: <LoansTable suffix={`loandetails/list/${orgId}`} />,
    };
  }

  if (organizationTypeId === GlobalConstants.OrganizationTypes.Vendor) {
    tabPages = {
      Comments: <CommentsTable suffix={commentsSuffix} orgId={orgId} />,
      Banks: <BanksTable suffix={`orgs/bank-infolist/${orgId}`} />,
      "State Tax": <StateTaxTable suffix={`orgs/state-taxes/${orgId}`} orgId={orgId} />,
      Contacts: <ContactsVendorTable suffix={`orgs/contact-orgs?orgId=${orgId}`} orgId={orgId} />,
      Borrowers: <BorrowersTable suffix={`orgs/org-borrowers/${orgId}`} />,
      Loans: <LoansTable suffix={`loandetails/list/${orgId}`} />
    };
  }

  if (organizationTypeId === GlobalConstants.OrganizationTypes.Builder) {
    tabPages = {
      Comments: <CommentsTable suffix={commentsSuffix} orgId={orgId} />,
      Banks: <BanksTable suffix={`orgs/bank-infolist/${orgId}`} />,
      Insurance: <InsuranceTable suffix={`orgs/insurances/${orgId}`} orgId={orgId} />,
      "State Tax": <StateTaxTable suffix={`orgs/state-taxes/${orgId}`} orgId={orgId} />,
      Loans: <LoansTable suffix={`loandetails/list/${orgId}`} />
    };
  }

  if (organizationTypeId === GlobalConstants.OrganizationTypes.Brokerage) {
    tabPages = {
      Contacts: <ContactsBrokerageTable suffix={`orgs/contacts/${orgId}`} orgId={orgId} />,
      Licenses: <LicensesTable suffix={`orgs/org-licenses/${orgId}`} orgId={orgId} />,
      Loans: <LoansTable suffix={`loandetails/list/${orgId}`} />
    };
  }

  if (organizationTypeId === GlobalConstants.OrganizationTypes["Loan Company"]) {
    tabPages = {
      Banks: <BanksTable suffix={`orgs/bank-infolist/${orgId}`}/>,
      "State Tax": <StateTaxTable suffix={`orgs/state-taxes/${orgId}`} orgId={orgId}/>,
      Loans: <LoansTable suffix={`loandetails/list/${orgId}`} />
    };
  }

  if (organizationTypeId === GlobalConstants.OrganizationTypes.Investor || organizationTypeId === GlobalConstants.OrganizationTypes.Fund) {
    tabPages = {
      Comments: <CommentsTable suffix={commentsSuffix} orgId={orgId}/>,
      Banks: <BanksTable suffix={`orgs/bank-infolist/${orgId}`}/>,
      "State Tax": <StateTaxTable suffix={`orgs/state-taxes/${orgId}`} orgId={orgId}/>,
      Loans: <LoansTable suffix={`loandetails/list/${orgId}`} />
    };
  }

  if (organizationTypeId === GlobalConstants.OrganizationTypes.Inspector) {
    tabPages = {
      Comments: <CommentsTable suffix={commentsSuffix} orgId={orgId}/>,
      "State Tax": <StateTaxTable suffix={`orgs/state-taxes/${orgId}`} orgId={orgId}/>,
      Loans: <LoansTable suffix={`loandetails/list/${orgId}`} />
    };
  }

  return <React.Fragment>
    <EditOrganization orgInfoData={orgInfoData} isLoading={isLoading} error={error} clearSuffixes={clearSuffixes} />
    {(orgInfoData && organizationTypeId && !skipTableTypes.includes(organizationTypeId) && tabPages) && <LmsTabPages tabPages={tabPages}/>}
  </React.Fragment>;
};

const LmsOrganizations = ({ ...props }: IDefaultPagesProps) => {
  const { params = {}, history } = props;
  const allowedActions = ["add", "edit", "copy"];
  const allowedSubpages = [
    "addaccount",
    "editaccount",
    "addtitlecompanyaccount",
    "edittitlecompanyaccount",
    "addcomment",
    "viewcomment",
    "addstatetax",
    "editstatetax",
    "addrouting",
    "editrouting",
    "addnewcontact",
    "addexistingcontact",
    "editcontact",
    "addinsurance",
    "editinsurance",
    "addlicense",
    "editlicense",
    "addloanproperty",
    "contact",
    "viewbank"
  ];

  const { isAdd, isEdit, actionLower, isMainPage, isSubpageAllowed, isActionAllowed, id, query } = useParseRouteActions(params, allowedActions, allowedSubpages);
  const orgId = query.get("orgId") || id;
  const mainTableSuffix = "orgs/info-list";
  if (!isActionAllowed) {
    history.goBack();
  }

  return <React.Fragment>
    {isMainPage && <LmsOrganizationsTable suffix={mainTableSuffix} />}
    {isAdd && <AddOrganization clearSuffixes={[mainTableSuffix, "orgs/list-orgs"]} />}
    {isEdit && <LmsOrganizationsEdit orgId={orgId} mainTableSuffix={mainTableSuffix} />}
    {isSubpageAllowed && <React.Fragment>
      {actionLower === "addaccount" && <AddAccountForm orgId={orgId} />}
      {actionLower === "editaccount" && <EditAccountForm id={id} />}
      {actionLower === "addtitlecompanyaccount" && <AddTitleCompanyAccountForm orgId={orgId} />}
      {actionLower === "edittitlecompanyaccount" && <EditTitleCompanyAccountForm id={id} />}
      {actionLower === "addrouting" && <AddRoutingForm orgId={orgId} />}
      {actionLower === "editrouting" && <EditRoutingForm id={id} />}
      {actionLower === "addcomment" && <AddCommentToOrganizationForm orgId={orgId} />}
      {actionLower === "viewcomment" && <ViewCommentForm id={id} />}
      {actionLower === "addstatetax" && <AddStateTaxForm orgId={orgId} />}
      {actionLower === "editstatetax" && <EditStateTaxForm id={id} />}
      {actionLower === "addexistingcontact" && <ExistingContactTable id={orgId} />}
      {actionLower === "addlicense" && <AddLicenseForm orgId={orgId}/>}
      {actionLower === "editlicense" && <EditLicenseForm id={id} />}
      {actionLower === "addinsurance" && <AddInsuranceForm orgId={orgId} />}
      {actionLower === "editinsurance" && <EditInsuranceForm id={id} />}
      {actionLower === "addloanproperty" && <AddLoanProperty orgId={orgId}/>}
      {actionLower === "viewbank" && <ViewBankForm id={id} />}
    </React.Fragment>}
  </React.Fragment>;
};

export default LmsOrganizations;